import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge, Button, Card, Field, Input } from '../ui';
import { Icon } from '../icons';
import { BRAND, getThemeColors } from '../../lib/theme';
import { mfaSetupApi } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { v1Admins } from '../../lib/v1';
import { formatDate } from '../../lib/dateFormat';
import type { MfaMethod, MfaSetting } from '../../types';

interface MfaMethodsManagerProps {
  dark: boolean;
}

const METHOD_LABEL_KEY: Record<MfaMethod, string> = {
  email: 'mfa.manager.method.email',
  sms:   'mfa.manager.method.sms',
  totp:  'mfa.manager.method.totp',
};

const METHOD_ICON: Record<MfaMethod, keyof typeof Icon> = {
  email: 'Mail',
  sms:   'Phone',
  totp:  'Settings',
};

/**
 * Drop-in MFA management widget. Shows the user's active methods, lets them
 * change the primary one, and lets them ADD methods (TOTP/SMS setup wizards
 * inline). Destructive ops (remove method, regenerate backup codes) require a
 * fresh Step-Up cookie + CSRF token and are gated with a hint to re-login.
 */
export function MfaMethodsManager({ dark }: MfaMethodsManagerProps) {
  const t = getThemeColors(dark);
  const { t: tr } = useTranslation();
  const { user } = useAuth();
  const [methods, setMethods] = useState<MfaSetting[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState<string | null>(null);

  // Setup wizards (inline)
  const [setupKind, setSetupKind] = useState<'totp' | 'sms' | null>(null);
  const [busy, setBusy]           = useState(false);

  // TOTP setup state
  const [totpSecret, setTotpSecret] = useState('');
  const [totpQrSvg,  setTotpQrSvg]  = useState('');
  const [totpCode,   setTotpCode]   = useState('');

  // SMS setup state — phone defaults to the mobile on the admin profile if set.
  const [smsPhone,       setSmsPhone]       = useState('');
  const [smsChallengeId, setSmsChallengeId] = useState('');
  const [smsCode,        setSmsCode]        = useState('');
  const [profileMobile,  setProfileMobile]  = useState<string | null>(null);

  const refresh = async () => {
    setLoading(true);
    setError(null);
    try {
      setMethods(await mfaSetupApi.getMethods());
    } catch (e) {
      setError(e instanceof Error ? e.message : tr('mfa.manager.error.load'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void refresh(); }, []);

  // Prefill the SMS phone field from the admin's profile mobile (if set).
  // Pulled once when the component mounts; refreshed if the user id changes.
  useEffect(() => {
    if (!user?.id) return;
    let alive = true;
    (async () => {
      try {
        const admin = await v1Admins.get(user.id);
        if (!alive) return;
        const m = (admin.mobile ?? '').trim();
        setProfileMobile(m || null);
        setSmsPhone(m || '');
      } catch { /* non-blocking — user can type the number manually */ }
    })();
    return () => { alive = false; };
  }, [user?.id]);

  const handleSetupEmail = async () => {
    setBusy(true);
    setError(null);
    try {
      await mfaSetupApi.setupEmail();
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : tr('mfa.manager.error.enable_email'));
    } finally {
      setBusy(false);
    }
  };

  const handleSetPrimary = async (method: MfaMethod) => {
    setError(null);
    try {
      await mfaSetupApi.setPrimary(method);
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : tr('mfa.manager.error.set_primary'));
    }
  };

  const handleStartTotp = async () => {
    setBusy(true);
    setError(null);
    try {
      const data = await mfaSetupApi.setupTotp();
      setTotpSecret(data.secret_setup_key);
      setTotpQrSvg(data.qr_code_svg ?? '');
      setSetupKind('totp');
    } catch (e) {
      setError(e instanceof Error ? e.message : tr('mfa.manager.error.totp_start'));
    } finally {
      setBusy(false);
    }
  };

  const handleConfirmTotp = async () => {
    if (totpCode.length !== 6) return;
    setBusy(true);
    setError(null);
    try {
      await mfaSetupApi.confirmTotp(totpCode);
      setSetupKind(null);
      setTotpCode('');
      setTotpSecret('');
      setTotpQrSvg('');
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : tr('mfa.manager.error.code_rejected'));
    } finally {
      setBusy(false);
    }
  };

  const handleStartSms = async () => {
    if (!smsPhone) return;
    setBusy(true);
    setError(null);
    try {
      const data = await mfaSetupApi.setupSms(smsPhone);
      setSmsChallengeId(data.challenge_id);
      setSetupKind('sms');
    } catch (e) {
      setError(e instanceof Error ? e.message : tr('mfa.manager.error.sms_start'));
    } finally {
      setBusy(false);
    }
  };

  const handleConfirmSms = async () => {
    if (smsCode.length !== 6 || !smsChallengeId) return;
    setBusy(true);
    setError(null);
    try {
      await mfaSetupApi.confirmSms(smsChallengeId, smsCode);
      setSetupKind(null);
      setSmsCode('');
      setSmsChallengeId('');
      setSmsPhone('');
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : tr('mfa.manager.error.code_rejected'));
    } finally {
      setBusy(false);
    }
  };

  const activeMethods   = methods.filter(m => m.enabled_at);
  const inactiveMethods: MfaMethod[] = (['email', 'sms', 'totp'] as const)
    .filter(m => !methods.some(s => s.method === m && s.enabled_at));

  return (
    <Card dark={dark} style={{ padding: 18 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 600, color: t.heading }}>{tr('mfa.manager.title')}</div>
          <div style={{ fontSize: 12, color: t.muted, marginTop: 2 }}>
            {tr('mfa.manager.subtitle')}
          </div>
        </div>
        <Badge tone={activeMethods.length > 0 ? 'success' : 'warning'} dark={dark}>
          {activeMethods.length > 0 ? tr('mfa.manager.active_count', { count: activeMethods.length }) : tr('mfa.manager.not_setup')}
        </Badge>
      </div>

      {error && (
        <div style={{ fontSize: 12, color: '#b42318', background: '#fef3f2', borderRadius: 8, padding: '8px 10px', marginBottom: 12 }}>
          {error}
        </div>
      )}

      {/* Active method list */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 14 }}>
        {loading && methods.length === 0 && (
          <div style={{ fontSize: 12, color: t.muted, padding: 8 }}>{tr('mfa.manager.loading')}</div>
        )}
        {activeMethods.map(s => {
          const IconComp = Icon[METHOD_ICON[s.method]];
          return (
            <div key={s.method} style={{
              display: 'flex', alignItems: 'center', gap: 12,
              padding: '10px 12px', background: t.cardMuted,
              border: `1px solid ${t.borderSoft}`, borderRadius: 10,
            }}>
              <div style={{ width: 32, height: 32, borderRadius: 8, background: t.card, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <IconComp c={BRAND} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 500, color: t.heading, display: 'flex', alignItems: 'center', gap: 6 }}>
                  {tr(METHOD_LABEL_KEY[s.method])}
                  {s.is_primary && <Badge tone="brand" dark={dark}>{tr('mfa.manager.primary')}</Badge>}
                </div>
                <div style={{ fontSize: 11, color: t.muted, marginTop: 2 }}>
                  {s.method === 'sms' && s.sms_number_masked
                    ? s.sms_number_masked
                    : s.enabled_at
                      ? tr('mfa.manager.activated_on', { date: formatDate(s.enabled_at) })
                      : '—'}
                </div>
              </div>
              {!s.is_primary && (
                <Button variant="secondary" size="sm" dark={dark} onClick={() => handleSetPrimary(s.method)}>
                  {tr('mfa.manager.set_primary')}
                </Button>
              )}
            </div>
          );
        })}
      </div>

      {/* Add new method buttons */}
      {inactiveMethods.length > 0 && !setupKind && (
        <div>
          <div style={{ fontSize: 12, fontWeight: 600, color: t.muted, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 8 }}>
            {tr('mfa.manager.add_more')}
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {inactiveMethods.includes('email') && (
              <Button variant="secondary" size="sm" dark={dark} onClick={handleSetupEmail} disabled={busy}>
                {tr('mfa.manager.enable_email')}
              </Button>
            )}
            {inactiveMethods.includes('totp') && (
              <Button variant="secondary" size="sm" dark={dark} onClick={handleStartTotp} disabled={busy}>
                {tr('mfa.manager.setup_totp')}
              </Button>
            )}
            {inactiveMethods.includes('sms') && (
              <Button variant="secondary" size="sm" dark={dark} onClick={() => setSetupKind('sms')} disabled={busy}>
                {tr('mfa.manager.setup_sms')}
              </Button>
            )}
          </div>
        </div>
      )}

      {/* TOTP setup wizard */}
      {setupKind === 'totp' && (
        <div style={{ padding: 14, border: `1px solid ${BRAND}`, borderRadius: 10, background: t.cardMuted, marginTop: 12 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: t.heading, marginBottom: 10 }}>
            {tr('mfa.manager.totp_step1')}
          </div>
          {totpQrSvg && (
            <img
              src={totpQrSvg.startsWith('data:') ? totpQrSvg : `data:image/svg+xml;utf8,${encodeURIComponent(totpQrSvg)}`}
              alt={tr('mfa.manager.qr_alt')}
              style={{ width: 180, height: 180, border: `1px solid ${t.border}`, borderRadius: 8, background: '#fff', padding: 8, display: 'block', marginBottom: 10 }}
            />
          )}
          <div style={{ fontSize: 11, color: t.muted, marginBottom: 8 }}>
            {tr('mfa.manager.totp_secret_hint')}
          </div>
          <code style={{ fontSize: 12, background: t.card, border: `1px solid ${t.border}`, borderRadius: 6, padding: '6px 10px', display: 'inline-block', marginBottom: 14, fontFamily: 'monospace' }}>
            {totpSecret}
          </code>
          <div style={{ fontSize: 13, fontWeight: 600, color: t.heading, marginBottom: 10 }}>
            {tr('mfa.manager.totp_step2')}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <Input
              value={totpCode}
              onChange={e => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="123456"
              dark={dark}
              style={{ width: 140, fontFamily: 'monospace', fontSize: 16, letterSpacing: '0.3em' }}
            />
            <Button variant="primary" dark={dark} onClick={handleConfirmTotp} disabled={busy || totpCode.length !== 6}>
              {busy ? tr('mfa.manager.checking') : tr('common.action.confirm')}
            </Button>
            <Button variant="secondary" dark={dark} onClick={() => { setSetupKind(null); setTotpCode(''); setTotpSecret(''); setTotpQrSvg(''); }}>
              {tr('common.action.cancel')}
            </Button>
          </div>
        </div>
      )}

      {/* SMS setup wizard */}
      {setupKind === 'sms' && (
        <div style={{ padding: 14, border: `1px solid ${BRAND}`, borderRadius: 10, background: t.cardMuted, marginTop: 12 }}>
          {!smsChallengeId ? (
            <>
              <div style={{ fontSize: 13, fontWeight: 600, color: t.heading, marginBottom: 10 }}>
                {tr('mfa.manager.sms_phone_title')}
              </div>
              <Field label={tr('pflege.form.field.mobile')}>
                <Input
                  value={smsPhone}
                  onChange={e => setSmsPhone(e.target.value)}
                  placeholder="+49 170 1234567"
                  dark={dark}
                />
              </Field>
              {profileMobile && (
                <div style={{ fontSize: 11, color: t.muted, marginTop: 6 }}>
                  {tr('mfa.manager.sms_prefill', { mobile: profileMobile })}
                </div>
              )}
              <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                <Button variant="primary" dark={dark} onClick={handleStartSms} disabled={busy || !smsPhone}>
                  {busy ? tr('mfa.manager.sending') : tr('mfa.manager.send_code')}
                </Button>
                <Button variant="secondary" dark={dark} onClick={() => { setSetupKind(null); setSmsPhone(''); }}>
                  {tr('common.action.cancel')}
                </Button>
              </div>
            </>
          ) : (
            <>
              <div style={{ fontSize: 13, fontWeight: 600, color: t.heading, marginBottom: 10 }}>
                {tr('mfa.manager.sms_code_title')}
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <Input
                  value={smsCode}
                  onChange={e => setSmsCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="123456"
                  dark={dark}
                  style={{ width: 140, fontFamily: 'monospace', fontSize: 16, letterSpacing: '0.3em' }}
                />
                <Button variant="primary" dark={dark} onClick={handleConfirmSms} disabled={busy || smsCode.length !== 6}>
                  {busy ? tr('mfa.manager.checking') : tr('common.action.confirm')}
                </Button>
                <Button variant="secondary" dark={dark} onClick={() => { setSetupKind(null); setSmsCode(''); setSmsChallengeId(''); setSmsPhone(''); }}>
                  {tr('common.action.cancel')}
                </Button>
              </div>
            </>
          )}
        </div>
      )}

      {/* Destructive ops hint */}
      {activeMethods.length > 0 && (
        <div style={{ marginTop: 14, paddingTop: 12, borderTop: `1px solid ${t.borderSoft}`, fontSize: 11, color: t.muted, lineHeight: 1.6 }}>
          {tr('mfa.manager.stepup_hint')}
        </div>
      )}
    </Card>
  );
}
